import classNames from "classnames"
import pluralize from "pluralize"
import PropTypes from "prop-types"
import React, { useCallback, useMemo, useState } from "react"
import { useMutation, useQueryClient } from "react-query"
import { useMarina } from "src/contexts/MarinaContext"
import AddLineItemButtons from "src/main/Service/Manage/shared/AddLineItemButtons"
import { buildJobLineItemColumns } from "src/main/Service/Manage/shared/jobLineItemColumns"

import Button from "src/components/Button"
import CollapsibleBar from "src/components/CollapsibleBar"
import DataTableV2 from "src/components/DataTableV2"
import ActionConfirmModal from "src/components/DataTableV2/ActionConfirmModal"
import Tooltip from "src/components/Tooltip"

import {
  deleteJobLineItem,
  getJobLineItems,
} from "src/api/Service/JobLineItems"
import { deleteJob } from "src/api/Service/Jobs"

import useAddJobNote from "src/hooks/use_add_job_note"
import useDatatable from "src/hooks/use_datatable"
import useJobActivity from "src/hooks/use_job_activity"
import { useToast } from "src/hooks/use_toast"
import { useTracker } from "src/hooks/use_tracker"

import { formatCurrencyFromMinorUnits } from "src/utils/UnitConversion"

import AddJobLineItemModal from "./AddJobLineItemModal"
import EditJobLineItemModal from "./EditJobLineItemModal"
import EditJobModal from "./EditJobModal"
import JobActivityFeed from "./JobActivityFeed"
import JobStatusBadge from "./JobStatusBadge"
import JobTransitionButton from "./JobTransitionButton"
import useJobTransition from "./hooks/use_job_transition"

const LOCK_TOOLTIP = "Reopen this job to edit."
const INVOICED_LOCK_TOOLTIP = "Invoiced jobs can't be edited."

const JobCollapsibleBar = ({
  job,
  workOrderSequentialId,
  boatLengthFeet,
  defaultOpen = false,
  isClosed = false,
}) => {
  const { marinaSlug, presentmentCurrency } = useMarina()
  const showToast = useToast()
  const queryClient = useQueryClient()
  const tracker = useTracker()

  const [lineItemModal, setLineItemModal] = useState({
    isOpen: false,
    availability: null,
    title: null,
  })

  const [editModal, setEditModal] = useState({
    isOpen: false,
    lineItem: null,
    initialFocus: "rate",
  })

  const openEditModal = useCallback(
    (row, initialFocus = "rate", source = "row_action") => {
      tracker.trackEvent("work_orders:edit_line_item:opened", {
        source,
        lineItemId: row.id,
        productId: row.product?.id,
        productType: row.productType,
      })
      setEditModal({ isOpen: true, lineItem: row, initialFocus })
    },
    [tracker]
  )

  const closeEditModal = useCallback(
    () => setEditModal({ isOpen: false, lineItem: null, initialFocus: "rate" }),
    []
  )

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)

  const isInvoiced = job.status === "invoiced"
  const isLocked = job.status === "completed" || isInvoiced || isClosed
  const lockTooltip = isInvoiced ? INVOICED_LOCK_TOOLTIP : LOCK_TOOLTIP

  const { start, complete, reopen, hold, resume, isTransitioning } =
    useJobTransition({
      jobId: job.id,
      workOrderSequentialId,
    })

  const handleTransition = (name) => {
    tracker.trackEvent(`work_orders:${name}_job:clicked`, {
      jobId: job.id,
      workOrderSequentialId,
    })
    if (name === "start") return start()
    if (name === "complete") return complete()
    if (name === "reopen") return reopen()
    if (name === "resume") return resume()
    return undefined
  }

  const handleHold = (holdReason, mutateOptions) => {
    tracker.trackEvent("work_orders:hold_job:clicked", {
      jobId: job.id,
      workOrderSequentialId,
    })
    hold(holdReason, mutateOptions)
  }

  const {
    data,
    records,
    tableProps,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useDatatable({
    queryKey: ["jobLineItems", marinaSlug, job.id],
    fetchFn: (params) => getJobLineItems(marinaSlug, job.id, params),
    syncFiltersToUrl: false,
    infinite: true,
  })

  const {
    entries: activityEntries,
    isLoading: activityIsLoading,
    isError: activityIsError,
    hasNextPage: activityHasNextPage,
    isFetchingNextPage: activityIsFetchingNextPage,
    fetchNextPage: fetchActivityNextPage,
  } = useJobActivity(marinaSlug, job.id)

  const { mutate: submitDelete, isLoading: isDeleting } = useMutation(
    () => deleteJob(marinaSlug, workOrderSequentialId, job.id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          "workOrder",
          marinaSlug,
          workOrderSequentialId,
        ])
        setIsDeleteModalOpen(false)
      },
      onError: () => {
        showToast("Couldn't delete this job. Please try again.", {
          type: "error",
        })
      },
    }
  )

  const { mutate: submitDeleteLineItem } = useMutation(
    (lineItem) => deleteJobLineItem(marinaSlug, job.id, lineItem.id),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(["jobLineItems", marinaSlug, job.id])
        queryClient.invalidateQueries([
          "workOrder",
          marinaSlug,
          workOrderSequentialId,
        ])
        showToast(data.message, { type: "success" })
      },
      onError: (error) => {
        showToast(error.message, { type: "error" })
      },
    }
  )

  const jobSubtotal = data?.jobSubtotal ?? 0
  const lineItemCount = data?.lineItemCount ?? 0

  const { addNote, isAdding: isAddingNote } = useAddJobNote(job.id)
  const handleAddNote = useCallback(
    (body, { onSuccess }) => {
      addNote(body, {
        onSuccess: () => {
          tracker.trackEvent("work_orders:add_note:saved", {
            jobId: job.id,
            workOrderSequentialId,
          })
          onSuccess?.()
        },
        onError: (error) => {
          tracker.trackEvent("work_orders:add_note:save_failed", {
            jobId: job.id,
            workOrderSequentialId,
            message: error?.message,
          })
        },
      })
    },
    [addNote, job.id, tracker, workOrderSequentialId]
  )

  const handleScroll = useCallback(
    (event) => {
      if (!hasNextPage || isFetchingNextPage) return
      const { scrollHeight, scrollTop, clientHeight } = event.target
      if (scrollHeight - scrollTop - 200 <= clientHeight) {
        fetchNextPage()
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage]
  )

  const columns = useMemo(
    () =>
      buildJobLineItemColumns({
        presentmentCurrency,
        onEditNote: isLocked
          ? undefined
          : (row) => openEditModal(row, "notes", "note_subrow"),
      }),
    [isLocked, presentmentCurrency, openEditModal]
  )

  const tableActions = useMemo(
    () =>
      isLocked
        ? []
        : [
            {
              label: "Edit",
              icon: "edit-square-md",
              onClickSingle: (row) => openEditModal(row, "rate", "row_action"),
            },
            {
              label: "Delete",
              icon: "sf-trashcan",
              danger: true,
              confirm: {
                title: "Delete Item",
                message: (row) => {
                  const subtotal = formatCurrencyFromMinorUnits({
                    amount: row.subtotal,
                    currency: presentmentCurrency,
                    precision: 2,
                  })
                  return `Delete "${row.product?.name}" (${subtotal})? This action cannot be undone.`
                },
                confirmLabel: "Delete Item",
              },
              onClickSingle: (row) => {
                tracker.trackEvent("work_orders:delete_line_item:confirmed", {
                  lineItemId: row.id,
                  productId: row.product?.id,
                  productType: row.productType,
                })
                submitDeleteLineItem(row)
              },
            },
          ],
    [
      isLocked,
      openEditModal,
      presentmentCurrency,
      submitDeleteLineItem,
      tracker,
    ]
  )

  const summary = (
    <div className="hidden items-center justify-end gap-3 text-xs text-gray-500 md:flex">
      <span className="whitespace-nowrap">
        {lineItemCount} {pluralize("line item", lineItemCount)}
      </span>
      <span className="whitespace-nowrap text-sm font-semibold text-gray-600">
        {formatCurrencyFromMinorUnits({
          amount: jobSubtotal,
          currency: presentmentCurrency,
          precision: 2,
        })}
      </span>
    </div>
  )

  const showHoldBanner = job.status === "on_hold" && Boolean(job.holdReason)

  const subtitle = job.description
    ? (isOpen) => (
        <span
          className={classNames(
            "text-xs text-gray-500",
            isOpen ? "block" : "line-clamp-2"
          )}
        >
          {job.description}
        </span>
      )
    : null

  const actions = (
    <div className="flex items-center gap-2">
      {!isClosed && (
        <JobTransitionButton
          job={job}
          onTransition={handleTransition}
          onHold={handleHold}
          disabled={isTransitioning}
        />
      )}
      {isLocked ? (
        <Tooltip text={lockTooltip} variant="dark">
          <span
            aria-label="Job locked"
            className="inline-flex h-7 w-7 items-center justify-center text-gray-500"
          >
            <i className="icon icon-lock" aria-hidden="true" />
          </span>
        </Tooltip>
      ) : (
        <>
          <Button
            type="button"
            title={`Edit ${job.name} job`}
            variant="ghost-tertiary"
            iconOnly
            small
            icon="icon-edit-square-md text-base"
            onClick={() => setIsEditModalOpen(true)}
          />
          <Button
            type="button"
            title={`Delete ${job.name} job`}
            variant="ghost-danger"
            iconOnly
            small
            icon="icon-sf-trashcan"
            onClick={() => setIsDeleteModalOpen(true)}
          />
        </>
      )}
    </div>
  )

  const openModal = (availability, title) => {
    setLineItemModal({ isOpen: true, availability, title })
  }

  const closeModal = () => {
    setLineItemModal({ isOpen: false, availability: null, title: null })
  }

  const bar = (
    <CollapsibleBar
      title={<span className="text-base text-gray-900">{job.name}</span>}
      badge={<JobStatusBadge status={job.status} />}
      summary={summary}
      subtitle={subtitle}
      banner={
        showHoldBanner ? { text: job.holdReason, type: "warning" } : undefined
      }
      actions={actions}
      ariaLabel={`Job: ${job.name}`}
      defaultOpen={defaultOpen}
      persistOpenState
      openStateStorageKey={`collapsibleBar.open.job-${job.id}`}
      alwaysShowSummary
    >
      <div className="-mt-1 md:grid md:grid-cols-12">
        <div className="col-span-12 min-w-0 xl:col-span-9 xl:pr-6">
          <h3 className="my-0 pb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            Line items
          </h3>
          <div className="mb-3 mt-5 w-full border-b border-gray-200" />
          <DataTableV2
            name={`job-${job.id}-line-items`}
            columns={columns}
            data={records}
            actions={tableActions}
            {...tableProps}
            hideSettings
            onScroll={handleScroll}
            emptyState={
              <div className="py-4 text-center text-sm text-gray-500">
                No line items yet. Add labor or parts to this job.
              </div>
            }
            footerRow={{
              productName: "Subtotal",
              subtotal: formatCurrencyFromMinorUnits({
                amount: jobSubtotal,
                currency: presentmentCurrency,
                precision: 2,
              }),
            }}
          />

          {!isLocked && (
            <div className="mt-3">
              <div className="mb-3 mt-5 w-full border-b border-gray-200" />
              <AddLineItemButtons
                onAddLabor={() => openModal("service_labor", "Add Labor")}
                onAddParts={() => openModal("service_parts", "Add Parts")}
              />
            </div>
          )}
        </div>

        <div className="col-span-12 mt-6 border-t border-gray-200 xl:col-span-3 xl:mt-0 xl:border-l xl:border-t-0">
          <JobActivityFeed
            entries={activityEntries}
            isLoading={activityIsLoading}
            isError={activityIsError}
            hasNextPage={activityHasNextPage}
            isFetchingNextPage={activityIsFetchingNextPage}
            fetchNextPage={fetchActivityNextPage}
            onAddNote={handleAddNote}
            isAddingNote={isAddingNote}
          />
        </div>
      </div>
    </CollapsibleBar>
  )

  return (
    <>
      {bar}

      {lineItemModal.isOpen && (
        <AddJobLineItemModal
          isOpen={lineItemModal.isOpen}
          onClose={closeModal}
          jobId={job.id}
          availability={lineItemModal.availability}
          modalTitle={lineItemModal.title}
          boatLengthFeet={boatLengthFeet}
        />
      )}

      <EditJobLineItemModal
        isOpen={editModal.isOpen}
        onClose={closeEditModal}
        jobId={job.id}
        lineItem={editModal.lineItem}
        boatLengthFeet={boatLengthFeet}
        initialFocus={editModal.initialFocus}
      />

      <ActionConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={() => submitDelete()}
        title="Delete Job"
        message={`Are you sure you want to delete "${job.name}"? This will remove all line items associated with this job. This action cannot be undone.`}
        confirmLabel="Delete Job"
        danger
        isLoading={isDeleting}
      />

      <EditJobModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        job={job}
        workOrderSequentialId={workOrderSequentialId}
      />
    </>
  )
}

JobCollapsibleBar.propTypes = {
  job: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    status: PropTypes.string,
    holdReason: PropTypes.string,
    permittedTransitions: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  workOrderSequentialId: PropTypes.string.isRequired,
  boatLengthFeet: PropTypes.number,
  defaultOpen: PropTypes.bool,
  isClosed: PropTypes.bool,
}

export default JobCollapsibleBar
